import type { CalendarRecord } from "@qendo/calendar";
import type { JournalData } from "@qendo/profile/journal";
import { type ReportablePeriod, type ReportData, ReportingPeriod } from "@qendo/profile/reports";
import { type DateArg, isSameDay } from "date-fns";
import { Stack, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { ComparisonSelector } from "~/components/form/reports/ComparisonSelector";
import { ReportPeriodSelector } from "~/components/form/reports/ReportPeriodSelector";
import { CalendarGroup } from "~/components/layout/calendar/CalendarGroup";
import { Page } from "~/components/layout/Page";
import { AnnualReport } from "~/components/layout/reports/AnnualReport";
import { MonthlyReport } from "~/components/layout/reports/MonthlyReport";
import { WeeklyReport } from "~/components/layout/reports/WeeklyReport";
import { Dismiss } from "~/components/permission/Dismiss";
import { Gap, Padding, Rounded } from "~/constants/layout";
import { useJournal } from "~/providers/JournalProvider";
import { useReport } from "~/providers/ReportProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { daysForPeriod, formatDateString } from "~/util/dates";

export default function ShowReport() {
    const { id }: { id: string } = useLocalSearchParams();
    const { background } = useTheme();
    const { journals } = useJournal();
    const { reports, getReport } = useReport();
    const [period, setPeriod] = useState<ReportablePeriod>(ReportingPeriod.last_week);
    const [compare, setCompare] = useState<string[]>([]);
    const [data, setData] = useState<ReportData | null>(null);
    const [from, setFrom] = useState<DateArg<Date> | null>(null);

    const report = useMemo(() => reports.find((data) => data.id === id), [id, reports]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(getReport): api
    useEffect(() => {
        if (report) {
            getReport({
                trackable_id: report.trackable_id,
                period,
                from,
                compare,
            }).then(setData);
        }
    }, [from, report, period, compare]);

    const calendar: CalendarRecord<JournalData> | null = useMemo(() => {
        if (data) {
            const find = [report?.trackable_id, ...compare];
            const start = data.start_at;
            const end = data.end_at;
            const dates = daysForPeriod(start, end);

            return Object.fromEntries(
                dates.map((day) => [
                    formatDateString(day),
                    journals.filter(({ trackable_id, recorded_on }) => {
                        return find.includes(trackable_id) && isSameDay(day, recorded_on);
                    }),
                ]),
            );
        }

        return null;
    }, [journals, data, report?.trackable_id, compare]);

    if (!report) {
        return <Dismiss />;
    }

    return (
        <>
            <Stack.Screen
                options={{
                    title: `${report.title} report`,
                }}
            />
            <Page spacer scrollable={false}>
                <View style={[background.primary, Padding.md, Gap.lg, Rounded.md]}>
                    <ReportPeriodSelector
                        period={period}
                        onUpdatePeriod={setPeriod}
                        from={from}
                        onUpdateFrom={setFrom}
                    />
                    {data?.period && [ReportingPeriod.last_week, ReportingPeriod.weekly].includes(data.period) && (
                        <WeeklyReport report={report} data={data} />
                    )}
                    {data?.period === ReportingPeriod.monthly && <MonthlyReport report={report} data={data} />}
                    {data?.period === ReportingPeriod.yearly && <AnnualReport report={report} data={data} />}
                    <ComparisonSelector report={report} selected={compare} data={data} onChangeSelected={setCompare} />
                </View>
                <View style={{ flex: 1 }}>{calendar && <CalendarGroup calendar={calendar} from={from} />}</View>
            </Page>
        </>
    );
}
